import { DataTable, DataTableSortStatus } from 'mantine-datatable';
import { useEffect, useLayoutEffect, useState } from 'react';
import {
  Link,
  useOutletContext,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import { ORDER_STATUS_COLORS, ORDER_STATUS_STRINGS } from '@domain/orderStatus';
import {
  PRODUCT_STATUS_COLORS,
  PRODUCT_STATUS_STRINGS,
} from '@domain/productStatus';
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Center,
  Divider,
  Grid,
  Group,
  Image,
  Loader,
  Modal,
  Space,
  Stepper,
  Text,
  Textarea,
  Title,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { openModal } from '@mantine/modals';
import { showNotification } from '@mantine/notifications';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import {
  IconAlertCircle,
  IconCircleCheck,
  IconExternalLink,
  IconHourglass,
  IconPencil,
  IconReload,
} from '@tabler/icons-react';
import { dateToDateHourString, dateToDateString } from '@utils/date';
import { priceToDecimal } from '@utils/price';

import { OrderProduct } from '@interfaces/orders.interface';

import {
  GetOrdersRequestParams,
  GetOrdersSortBy,
  useCancelProductMutation,
  useConfirmOrderMutation,
  useElaborateOrderMutation,
  useGetOrderProductsCountQuery,
  useGetOrderProductsQuery,
  useGetSingleOrderQuery,
} from '@api/orders.api';

import Forbidden from '@routes/Forbidden';

import Card from '@components/card/Card';
import EditOrderShippingAddressForm from '@components/EditOrderShippingAddressForm';
import GraphicsDraftsReview from '@components/graphicsDraftReview/GraphicsDraftsReview';
import { LayoutContextType } from '@components/layout/Layout';
import OrderPaymentSection from '@components/orderPaymentSection/OrderPaymentSection';
import SearchBar from '@components/SearchBar';

import NotFound from '../NotFound';
import useUserRoleGuard from '@hooks/useUserRoleGuard';

const statusIndexMap = {
  pending: 0,
  inserted: 0,
  waitingConfirmation: 1,
  waitingPayment: 2,
  confirmed: 3,
  production: 4,
  closed: 5,
};

export default function OrderDetail() {
  // ==========================================================================
  // General
  // ==========================================================================
  const { id } = useParams();

  const userIsAdmin = useUserRoleGuard('admin');

  const TRANSITION_DURATION = 200;

  const [searchParams] = useSearchParams();

  // ==========================================================================
  // State
  // ==========================================================================
  const [paymentModalOpened, setPaymentModalOpened] = useState(false);

  const [graphicsModalOpened, setGraphicsModalOpened] = useState(false);
  const [modalGraphicsInfo, setModalGraphicsInfo] = useState<{
    productName: string;
    draftProductsRelationId: string;
  } | null>(null);

  const [filters, setFilters] = useState<GetOrdersRequestParams>({
    page: +(searchParams.get('page') || 1),
    pageLength: +(searchParams.get('pageLength') || 5),
    searchQuery: searchParams.get('search') || undefined,
    sortBy: 'id',
    sortOrder: 'asc',
  });

  const [searchQuery] = useDebouncedValue(filters.searchQuery, 200, {
    leading: true,
  });

  const [sortStatus, setSortStatus] = useState<
    DataTableSortStatus<OrderProduct>
  >({
    columnAccessor: 'name',
    direction: 'asc',
  });

  useEffect(() => {
    if (
      filters.sortBy === sortStatus.columnAccessor &&
      filters.sortOrder === sortStatus.direction
    ) {
      return;
    }

    setFilters((filters) => ({
      ...filters,
      sortBy: sortStatus.columnAccessor as GetOrdersSortBy,
      sortOrder: sortStatus.direction,
    }));
  }, [sortStatus]);

  // ==========================================================================
  // Api
  // ==========================================================================
  const {
    data: order,
    isLoading,
    error,
    //  refetch: refetchOrder,
  } = useGetSingleOrderQuery(id!);

  const [confirmOrder, { isLoading: isLoadingConfirmOrder }] =
    useConfirmOrderMutation();

  const [cancelProduct, { isLoading: isCancelProductLoading }] =
    useCancelProductMutation();

  // Get initial products count
  const {
    data: productsCount = { count: 0 },
    isLoading: isLoadingProductsCount,
  } = useGetOrderProductsCountQuery({ id: id!, searchQuery });

  const {
    data: productsData = [],
    isLoading: isProductsLoading,
    // refetch: refetchProducts,
  } = useGetOrderProductsQuery({
    id: id!,
    params: { ...filters, searchQuery },
  });

  const [elaborateOrder, { isLoading: isLoadingElaborateOrder }] =
    useElaborateOrderMutation();

  // ==========================================================================
  // Handlers
  // ==========================================================================
  const handleGraphicVerificationRequestClick = (product: OrderProduct) => {
    setGraphicsModalOpened(true);

    setModalGraphicsInfo({
      productName: product.name,
      draftProductsRelationId: product.draftProductsRelationId!,
    });
  };

  const handleProductCancelRequestClick = async (product: OrderProduct) => {
    try {
      await cancelProduct(product.id).unwrap();

      showNotification({
        title: 'Prodotto rimosso',
        message: 'Il prodotto è stato annullato con successo.',
      });
    } catch (e) {
      console.error(e);

      showNotification({
        title: "Errore nell'annullamento del prodotto!",
        message: "Impossibile procedere con l'annullamento.",
        color: 'red',
      });
    }
  };

  // const handlePaypalCreateOrder = async () => {};

  // const handlePaypalApprove = async (data: OnApproveData) => {};

  const handleConfirmOrderClick = async () => {
    try {
      await confirmOrder({ id: order!.id }).unwrap();
    } catch (e) {
      console.error(e);

      showNotification({
        title: "Errore nella conferma dell'ordine!",
        message: 'Impossibile procedere con la conferma.',
        color: 'red',
      });
    }
  };

  const handleEditShippingInformationsClick = () => {
    openModal({
      title: 'Modifica indirizzo di consegna',
      children: <EditOrderShippingAddressForm order={order!} />,
    });
  };

  const handleElaborateOrderClick = async () => {
    try {
      await elaborateOrder(order!.id).unwrap();
    } catch (e) {
      console.error(e);

      showNotification({
        title: "Errore nell'elaborazione dell'ordine!",
        message: "Impossibile procedere con l'elaborazione.",
        color: 'red',
      });
    }
  };

  // ==========================================================================
  // Render
  // ==========================================================================
  const totalPriceNoDiscount = order?.total || 0;
  const discount =
    order?.paymentType === 'bankTransferOnConfirm' ||
    order?.paymentType === 'other'
      ? order.subtotal * 0.03
      : 0;
  const netTotal = order ? order.subtotal + order.shippingCost - discount : 0;
  const taxes = netTotal * 0.22;
  const totalPrice = order ? order.netTotal + taxes : 0;

  const stepperSteps = Object.keys(ORDER_STATUS_STRINGS)
    .filter(
      (status) =>
        status !== 'processingPayment' &&
        (!order?.onlinePayedTotal || status !== 'waitingPayment'),
    )
    .map((status) => (
      <Stepper.Step
        key={status}
        label={
          ORDER_STATUS_STRINGS[status as keyof typeof ORDER_STATUS_STRINGS]
        }
        color={ORDER_STATUS_COLORS[status as keyof typeof ORDER_STATUS_STRINGS]}
      />
    ));

  const { setLayoutProps } = useOutletContext<LayoutContextType>();

  useLayoutEffect(() => {
    setLayoutProps({
      title: `Dettagli ordine ${order?.bluedayOrderId ? `#${order?.bluedayOrderId}` : ''}`,
      backLink: { title: 'Lista ordini', to: '/ordini' },
    });
  }, [setLayoutProps, order?.bluedayOrderId]);

  return isLoading || isProductsLoading || isLoadingProductsCount ? (
    <Center my="xl">
      <Loader />
    </Center>
  ) : !isLoading &&
    !isLoading &&
    (!order || (error && (error as FetchBaseQueryError).status === 404)) ? (
    <NotFound />
  ) : !isLoading &&
    (!order || (error && (error as FetchBaseQueryError).status === 403)) ? (
    <Forbidden />
  ) : (
    <>
      <Modal
        fullScreen
        opened={graphicsModalOpened}
        onClose={() => setGraphicsModalOpened(false)}
        title="Verifica grafica prodotto"
        transitionProps={{ duration: TRANSITION_DURATION }}
        styles={{
          body: {
            flexGrow: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          },
        }}
      >
        <GraphicsDraftsReview
          productName={modalGraphicsInfo?.productName}
          draftProductsRelationId={modalGraphicsInfo?.draftProductsRelationId}
          closeModal={() => setGraphicsModalOpened(false)}
          transitionDuration={TRANSITION_DURATION}
        />
      </Modal>

      <Modal
        opened={paymentModalOpened}
        onClose={() => setPaymentModalOpened(false)}
        title="Pagamento ordine"
      ></Modal>

      <>
        {!userIsAdmin && (
          <Stepper
            visibleFrom="lg"
            my={40}
            active={
              statusIndexMap[
                order?.status
                  ? order.status !== 'processingPayment'
                    ? order.status !== 'pending'
                      ? order.status !== 'refunded'
                        ? order.status
                        : 'inserted'
                      : 'closed'
                    : 'waitingPayment'
                  : 'inserted'
              ]
            }
            size="sm"
            styles={{
              step: {
                flexDirection: 'column',
              },
              stepBody: {
                marginLeft: '0',
                marginTop: '7px',
              },
            }}
          >
            {stepperSteps}
          </Stepper>
        )}

        {order!.status === 'processingPayment' && (
          <Alert title="Pagamento in corso" mb="lg" icon={<IconHourglass />}>
            Pagamento in corso. Ricaricando la pagina fra qualche secondo potrai
            vedere l'ordine come confermato.
          </Alert>
        )}

        {searchParams.get('payment_intent') &&
          searchParams.get('redirect_status') === 'succeeded' && (
            <Alert
              mb="lg"
              color="green"
              title="Pagamento confermato"
              icon={<IconCircleCheck />}
            >
              <Text>Pagamento avvenuto con successo e ordine confermato.</Text>
            </Alert>
          )}

        {!userIsAdmin && order!.status === 'waitingPayment' && (
          <>
            <OrderPaymentSection order={order!} />
            <Space h="xl" />
          </>
        )}

        <Grid>
          {userIsAdmin && order?.status === 'pending' && (
            <Grid.Col span={12}>
              <Alert icon={<IconAlertCircle />} title="Ordine non elaborato">
                <Text size="sm">
                  L'ordine non è stato correttamente elaborato dal gestionale.
                  Per provare a rielaborarlo, clicca sul pulsante sottostante.
                </Text>
                <Button
                  mt="md"
                  leftSection={<IconReload />}
                  onClick={handleElaborateOrderClick}
                  loading={isLoadingElaborateOrder}
                >
                  Rialabora ordine
                </Button>
              </Alert>
            </Grid.Col>
          )}
          {userIsAdmin &&
            order!.relatedBluedayLogs &&
            order!.relatedBluedayLogs.length > 0 && (
              <Grid.Col span={12}>
                <Card title="Log comunicazioni FM">
                  <DataTable
                    withTableBorder
                    striped
                    h={150}
                    columns={[
                      {
                        accessor: 'date',
                        title: 'Data e ora',
                        render: (record) =>
                          dateToDateHourString(new Date(record.date)),
                      },
                      { accessor: 'name', title: 'Nome' },
                      {
                        accessor: 'responseStatusCode',
                        title: 'Stato',
                        render: (record) => (
                          <Badge
                            color={
                              record.responseStatusCode === '200'
                                ? 'green'
                                : 'red'
                            }
                          >
                            {record.responseStatusCode === '200' ||
                            record.responseStatusCode === '201'
                              ? 'OK'
                              : 'Fallito'}
                          </Badge>
                        ),
                      },
                      {
                        accessor: 'debug',
                        title: 'Informazioni debug',
                        render: (record) => (
                          <Textarea
                            value={`API:\n${record.api}\n\nRequest body:\n${record.requestBody}\n\nResponse status code:\n${record.responseStatusCode}\n\nResponse data:\n${record.responseData}`}
                            readOnly
                          />
                        ),
                      },
                    ]}
                    records={order!.relatedBluedayLogs}
                  />
                </Card>
              </Grid.Col>
            )}

          {/* GRID ROW */}
          <Grid.Col span={{ lg: 6, xl: 4 }}>
            <Card title="Informazioni ordine">
              <Group justify="space-between" hiddenFrom="lg">
                <Text fw="bold">Stato ordine</Text>
                <Badge color={ORDER_STATUS_COLORS[order!.status]}>
                  {ORDER_STATUS_STRINGS[order!.status]}
                </Badge>
              </Group>

              {userIsAdmin && (
                <Group justify="space-between" gap={0}>
                  <Text fw="bold">Id ordine</Text>
                  <Text>{order!.id}</Text>
                </Group>
              )}

              <Group justify="space-between" gap={0} mt="xs">
                <Text fw="bold">Numero ordine</Text>
                <Text>{order!.bluedayOrderId || order!.id || '-'}</Text>
              </Group>

              {userIsAdmin && (
                <>
                  <Group justify="space-between" mt="xs">
                    <Text fw="bold">Cliente</Text>
                    <Anchor
                      to={`/utenti/${order!.client?.user.id}`}
                      component={Link}
                    >
                      {order!.client?.user.name}
                    </Anchor>
                  </Group>
                  <Group justify="space-between" mt="xs">
                    <Text fw="bold">Stato ordine</Text>
                    <Badge color={ORDER_STATUS_COLORS[order!.status]}>
                      {ORDER_STATUS_STRINGS[order!.status]}
                    </Badge>
                  </Group>
                </>
              )}

              <Group justify="space-between" gap={0} mt="xs">
                <Text fw="bold">Riferimento cliente</Text>
                <Text>{order!.customerRef || '-'}</Text>
              </Group>
              <Group justify="space-between" gap={0} mt="xs">
                <Text fw="bold">Data</Text>
                <Text>{dateToDateString(new Date(order!.date))}</Text>
              </Group>
              <Text fw="bold" mt="xs">
                Informazioni fatturazione
              </Text>
              <Text>
                {order!.invoicingName}, {order!.invoicingVatTaxNumber}
              </Text>
              {order!.invoicingPec ||
                (order!.invoicingSdi && (
                  <Text>
                    {order!.invoicingPec}, {order!.invoicingSdi}
                  </Text>
                ))}
              <Text>
                {order!.invoicingAddress} - {order!.invoicingZip},{' '}
                {order!.invoicingCity} ({order!.invoicingProvince})
              </Text>

              {order!.client && (
                <>
                  <Group justify="space-between" gap={0} mt="xs">
                    <Text fw="bold">Email cliente</Text>
                    <Text>{order!.client.user.email}</Text>
                  </Group>
                  <Group justify="space-between" gap={0} mt="xs">
                    <Text fw="bold">Telefono cliente</Text>
                    <Text>{order!.client.phone ?? '-'}</Text>
                  </Group>
                </>
              )}

              {order!.note && (
                <>
                  <Text fw="bold">Note ordine</Text>
                  <Text>{order!.note}</Text>
                </>
              )}

              {!userIsAdmin && order?.status === 'waitingConfirmation' && (
                <Button
                  leftSection={<IconCircleCheck />}
                  mt="lg"
                  onClick={handleConfirmOrderClick}
                  loading={isLoadingConfirmOrder}
                >
                  Conferma ordine
                </Button>
              )}
            </Card>
          </Grid.Col>

          {/* GRID ROW */}
          <Grid.Col span={{ lg: 6, xl: 4 }}>
            <Card
              title={`Indirizzo di ${order!.shippingMethod === 'courier' ? 'consegna' : 'ritiro'}`}
              action={
                !userIsAdmin &&
                order!.shippingMethod === 'courier' &&
                order!.status === 'inserted'
                  ? {
                      title: 'Modifica',
                      icon: <IconPencil />,
                      onClick: handleEditShippingInformationsClick,
                    }
                  : undefined
              }
            >
              {order!.shippingMethod === 'courier' ? (
                <>
                  <Text>{order!.shippingName}</Text>
                  <Text>{order!.shippingAddress}</Text>
                  <Text>
                    {order!.shippingZip} - {order!.shippingCity} (
                    {order!.shippingProvince}), {order!.shippingNation}
                  </Text>
                </>
              ) : order!.shippingMethod === 'handPickupShopBassano' ? (
                <>
                  <Text>Via Bellavitis, 35</Text>
                  <Text>36061 Bassano del Grappa (VI)</Text>
                </>
              ) : order!.shippingMethod === 'handPickupShopSpes' ? (
                <>
                  <Text>Via Palazzetto, 14</Text>
                  <Text>36026 Pojana Maggiore VI</Text>
                </>
              ) : order!.shippingMethod === 'handPickupWhyCenterValdagno' ? (
                <>
                  <Text>Corso Italia, 14</Text>
                  <Text>36078 Valdagno (VI)</Text>
                </>
              ) : order!.shippingMethod === 'handPickupWhyCenterSchio' ? (
                <>
                  <Text>Viale dell'indutria, 18</Text>
                  <Text>36015 Schio (VI)</Text>
                </>
              ) : (
                <>
                  <Text>Via dell'Artigianato, 41/A</Text>
                  <Text>36030 - Villaverla (VI), Italia</Text>
                </>
              )}
            </Card>
          </Grid.Col>

          {/* GRID ROW */}
          <Grid.Col span={{ lg: 6, xl: 4 }}>
            <Card title="Spedizione e pagamento">
              <Group justify="space-between">
                <Text fw="bold">Tipologia spedizione</Text>
                <Text>
                  {order!.shippingMethod === 'courier'
                    ? 'Corriere'
                    : 'Ritiro in sede'}
                </Text>
              </Group>

              {order!.shippingMethod === 'courier' && (
                <>
                  <Group justify="space-between">
                    <Text fw="bold">Data spedizione prevista</Text>
                    <Text>
                      {order!.shippingDate
                        ? dateToDateString(new Date(order!.shippingDate))
                        : 'Non disponibile'}
                    </Text>
                  </Group>
                  <Group justify="space-between">
                    <Text fw="bold">Tracciamento spedizione</Text>
                    <Text>
                      {order!.trackingUrl && order!.trackingUrl.length > 0
                        ? order!.trackingUrl.map((url, i) => (
                            <Anchor
                              key={url}
                              href={url}
                              style={{
                                display:
                                  order!.trackingUrl!.length > 1
                                    ? 'block'
                                    : 'initial',
                              }}
                            >
                              <Group gap={5} style={{ display: 'inline-flex' }}>
                                Vai al sito{' '}
                                {order!.trackingUrl!.length > 1 ? i : ''}
                                <IconExternalLink size={20} />
                              </Group>
                            </Anchor>
                          ))
                        : 'Non disponibile'}
                    </Text>
                  </Group>
                </>
              )}
              <Divider my="md" />
              <Group justify="space-between">
                <Text fw="bold">Tipologia pagamento</Text>
                <Text>
                  {order!.paymentType === 'onlineOnConfirm' ? (
                    'Pagamento online'
                  ) : order?.paymentType === 'onlineMollie' ? (
                    'Pagamento online'
                  ) : (
                    <Group style={{ display: 'inline-flex' }} gap="xs">
                      Bonifico bancario
                    </Group>
                  )}
                </Text>
              </Group>

              <Group justify="space-between" mt="xs">
                <Text fw="bold">Subtotale</Text>
                <Text ta="right">€ {priceToDecimal(order!.subtotal)}</Text>
              </Group>
              {order!.shippingMethod === 'courier' && (
                <Group justify="space-between">
                  <Text fw="bold">Costo spedizione</Text>
                  <Text ta="right">
                    € {priceToDecimal(order!.shippingCost)}
                  </Text>
                </Group>
              )}
              {order!.couponDiscount !== 0 && (
                <Group justify="space-between">
                  <Text fw="bold">Sconto</Text>
                  <Text ta="right">
                    - € {priceToDecimal(order!.couponDiscount)}
                  </Text>
                </Group>
              )}
              <Group justify="space-between">
                <Text fw="bold">Totale netto</Text>
                <Text ta="right">€ {priceToDecimal(order!.netTotal)}</Text>
              </Group>
              <Group justify="space-between">
                <Text fw="bold">Tasse</Text>
                <Text ta="right">€ {priceToDecimal(order!.taxes)}</Text>
              </Group>
              <Group justify="space-between" mt="xs">
                <Text fw="bold">Totale (Iva In.)</Text>
                <Text ta="right">
                  {order?.status === 'inserted' ||
                  order?.status === 'waitingConfirmation' ||
                  order?.status === 'waitingPayment' ? (
                    <>
                      € {priceToDecimal(totalPriceNoDiscount)}
                      <br />
                      {(order!.paymentType === 'bankTransferOnConfirm' ||
                        order!.paymentType === 'other') && (
                        <>(€ {priceToDecimal(totalPrice)} pagando online)</>
                      )}
                    </>
                  ) : order?.total !== totalPriceNoDiscount ? (
                    <>
                      <span style={{ textDecoration: 'line-through' }}>
                        € {priceToDecimal(totalPriceNoDiscount)}
                      </span>{' '}
                      {order!.paymentType !== 'onlineOnConfirm' && (
                        <>€ {priceToDecimal(order!.total)}</>
                      )}
                    </>
                  ) : (
                    <>€ {priceToDecimal(totalPriceNoDiscount)}</>
                  )}
                </Text>
              </Group>

              {order!.refundedAmount && (
                <Group justify="space-between">
                  <Text fw="bold">Importo rimborsato</Text>
                  <Text ta="right">
                    € {priceToDecimal(order!.refundedAmount)}
                  </Text>
                </Group>
              )}

              {!userIsAdmin &&
                order!.status === 'waitingPayment' &&
                (order!.paymentType === 'bankTransferOnConfirm' ? (
                  <Alert variant="filled" ta="justify" mt="md">
                    Puoi scegliere di pagare subito il tuo ordine tramite carta
                    di credito o paypal ed ottenere uno sconto del 3%, oppure
                    proseguire con il pagamento tramite bonifico bancario.
                  </Alert>
                ) : (
                  order!.paymentType === 'other' && (
                    <Alert variant="filled" ta="justify" mt="md">
                      Puoi scegliere di pagare subito il tuo ordine tramite
                      carta di credito o paypal ed ottenere uno sconto del 3%,
                      oppure proseguire con il pagamento tramite bonifico
                      bancario.
                    </Alert>
                  )
                ))}
            </Card>
          </Grid.Col>
        </Grid>

        <Title order={3} mt="xl">
          Prodotti
        </Title>
        <Group mt="lg" mb="md">
          <SearchBar
            placeholder="Ricerca per codice o nome prodotto"
            value={filters.searchQuery}
            onChange={(newValue) =>
              setFilters({ ...filters, searchQuery: newValue })
            }
          />
        </Group>
        <DataTable
          striped
          withTableBorder
          fetching={isProductsLoading || isLoadingProductsCount}
          noRecordsText="Nessun prodotto trovato"
          minHeight={productsData.length === 0 ? 200 : undefined}
          columns={[
            {
              accessor: 'imageUrl',
              title: '',
              width: '60px',
              render: (record) => (
                <Image
                  src={record.imageUrl}
                  alt={record.name}
                  h="2rem"
                  w="2rem"
                  fit="contain"
                />
              ),
            },
            {
              accessor: 'sku',
              title: 'Codice',
              sortable: true,
              render: (record) => record.sku || '-',
            },
            {
              accessor: 'name',
              title: 'Nome',
              sortable: true,
            },
            {
              accessor: 'color',
              title: 'Colore',
              render: (record) => record.color || '-',
            },
            {
              accessor: 'size',
              title: 'Taglia',
              render: (record) => record.size || '-',
            },
            {
              accessor: 'quantity',
              title: 'Qta.',
            },
            {
              accessor: 'totalPrice',
              title: 'Prezzo tot.',
              render: (record) => (
                <Group gap={3} wrap="nowrap">
                  <span>€</span>
                  {record.salePercent && (
                    <span>
                      <span style={{ textDecoration: 'line-through' }}>
                        {priceToDecimal(record.totalPrice)}
                      </span>
                    </span>
                  )}
                  <span>
                    {priceToDecimal(
                      record.totalPrice *
                        (1 - (record.salePercent || 0) / 1000),
                    )}
                  </span>
                </Group>
              ),
            },
            {
              accessor: 'salePercent',
              title: 'Sconto',
              render: (record) =>
                record.salePercent ? `${record.salePercent / 10}%` : '-',
            },
            {
              accessor: 'shippingDate',
              title: 'Data sped. prev.',
              render: (record) =>
                record.shippingDate
                  ? new Date(record.shippingDate).toLocaleDateString('IT-it')
                  : '-',
            },
            {
              accessor: 'status',
              title: 'Stato',
              render: (record) => (
                <>
                  <Badge color={PRODUCT_STATUS_COLORS[record.status]}>
                    {PRODUCT_STATUS_STRINGS[record.status]}
                  </Badge>
                  {!userIsAdmin &&
                    record.status === 'waitingGraphicsConfirmation' && (
                      <Button
                        style={{ display: 'block' }}
                        mt="xs"
                        onClick={() =>
                          handleGraphicVerificationRequestClick(record)
                        }
                      >
                        Verifica grafica
                      </Button>
                    )}
                  {!userIsAdmin &&
                    order &&
                    !order.onlinePayedTotal &&
                    (record.status === 'inserted' ||
                      record.status === 'checkingAvailability' ||
                      record.status === 'waitingConfirmation') && (
                      <Button
                        style={{ display: 'block' }}
                        mt="xs"
                        variant="default"
                        onClick={() => handleProductCancelRequestClick(record)}
                        loading={isCancelProductLoading}
                      >
                        Rimuovi prodotto
                      </Button>
                    )}
                </>
              ),
            },
          ]}
          records={productsData}
          sortStatus={sortStatus}
          onSortStatusChange={setSortStatus}
          page={filters.page!}
          recordsPerPage={filters.pageLength!}
          totalRecords={productsCount.count}
          paginationText={() => ''}
          recordsPerPageLabel=""
          recordsPerPageOptions={[5, 10, 20, 50]}
          onPageChange={(newPage) => setFilters({ ...filters, page: newPage })}
          onRecordsPerPageChange={(newPageLength) =>
            setFilters({ ...filters, pageLength: newPageLength, page: 1 })
          }
        />
      </>
    </>
  );
}
